/**
 * SEVO Animations JavaScript
 * Scroll-triggered animations, word reveals, and video controls
 */

(function() {
  'use strict';

  window.SEVO = window.SEVO || {};

  window.SEVO.Animations = {
    // Store references for cleanup
    _scrollHandlers: [],

    init: function() {
      // Clean up previous listeners before re-init (for Shopify section reload)
      this.cleanup();

      // Wrap each init in try-catch to prevent cascading failures
      const safeInit = (fn, name) => {
        try {
          fn.call(this);
        } catch (e) {
          // Silent fail in production - animation features are non-critical
          if (window.Shopify?.designMode) {
            console.error(`SEVO Animations: ${name} failed to initialize`, e);
          }
        }
      };

      safeInit(this.initScrollAnimations, 'ScrollAnimations');
      safeInit(this.initWordReveal, 'WordReveal');
      safeInit(this.initVideoHover, 'VideoHover');
      safeInit(this.initAccordions, 'Accordions');
      safeInit(this.initGallery, 'Gallery');
      safeInit(this.initSubscriptionToggle, 'SubscriptionToggle');
      safeInit(this.initQuantityButtons, 'QuantityButtons');
      safeInit(this.initVariantSelector, 'VariantSelector');
      safeInit(this.initParallax, 'Parallax');
      safeInit(this.initCountUp, 'CountUp');
      safeInit(this.initScrollProgress, 'ScrollProgress');
      safeInit(this.initStaggeredReveal, 'StaggeredReveal');
      safeInit(this.initSmoothScroll, 'SmoothScroll');
    },

    /**
     * Cleanup scroll handlers to prevent memory leaks on section reload
     */
    cleanup: function() {
      this._scrollHandlers.forEach(handler => {
        window.removeEventListener('scroll', handler);
      });
      this._scrollHandlers = [];
    },

    /**
     * Scroll-triggered fade-in animations using Intersection Observer
     */
    initScrollAnimations: function() {
      const animatedElements = document.querySelectorAll('[data-animate]');

      if (!animatedElements.length) return;

      const observerOptions = {
        root: null,
        rootMargin: '0px 0px -50px 0px',
        threshold: 0.1
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      animatedElements.forEach(el => {
        observer.observe(el);
      });
    },

    /**
     * Word-by-word reveal animation for headings
     */
    initWordReveal: function() {
      const wordRevealElements = document.querySelectorAll('[data-word-reveal]');

      wordRevealElements.forEach(el => {
        if (el.dataset.wordRevealInitialized) return;

        const text = el.textContent.trim();
        const words = text.split(/\s+/);

        el.innerHTML = words.map((word, i) => {
          const delay = i * 0.14;
          return `<span class="word"><span style="animation-delay: ${delay}s">${word}</span></span>`;
        }).join(' ');

        el.dataset.wordRevealInitialized = 'true';
      });
    },

    /**
     * Video autoplay on hover for product cards
     */
    initVideoHover: function() {
      const videoCards = document.querySelectorAll('[data-product-video]');

      videoCards.forEach(video => {
        const card = video.closest('.product-card');
        if (!card) return;

        card.addEventListener('mouseenter', () => {
          video.play().catch(() => {
            // Autoplay was prevented, ignore
          });
        });

        card.addEventListener('mouseleave', () => {
          video.pause();
          video.currentTime = 0;
        });
      });
    },

    /**
     * Accordion functionality
     */
    initAccordions: function() {
      const accordionTriggers = document.querySelectorAll('[data-accordion-trigger]');

      accordionTriggers.forEach(trigger => {
        // Skip if already initialized
        if (trigger.dataset.accordionInitialized) return;
        trigger.dataset.accordionInitialized = 'true';

        trigger.addEventListener('click', () => {
          const accordion = trigger.closest('[data-accordion]');
          const content = accordion
            ? accordion.querySelector('[data-accordion-content]')
            : trigger.nextElementSibling;
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          // Toggle current accordion
          trigger.setAttribute('aria-expanded', !isOpen);
          if (content) content.classList.toggle('is-open');
        });
      });
    },

    /**
     * Subscription toggle functionality
     * Handles both product pages and any other subscription toggles
     */
    initSubscriptionToggle: function() {
      const subscriptionContainers = document.querySelectorAll('[data-subscription-options]');

      subscriptionContainers.forEach(container => {
        // Skip if already initialized
        if (container.dataset.subscriptionInitialized) return;
        container.dataset.subscriptionInitialized = 'true';

        const toggleOptions = container.querySelectorAll('[data-purchase-type]');
        const sellingPlanInput = container.querySelector('[data-selling-plan-input]');

        // Store the original selling plan value for subscribe mode
        const originalSellingPlan = sellingPlanInput ? sellingPlanInput.value : '';

        // Initialize state based on which radio is checked
        const initState = () => {
          const checkedRadio = container.querySelector('[data-purchase-type]:checked');
          if (checkedRadio && sellingPlanInput) {
            const isSubscribe = checkedRadio.value === 'subscribe';
            if (isSubscribe) {
              sellingPlanInput.value = originalSellingPlan;
              sellingPlanInput.disabled = false;
            } else {
              sellingPlanInput.value = '';
              sellingPlanInput.disabled = true;
            }
          }
        };

        // Run initial state setup
        initState();

        toggleOptions.forEach(option => {
          option.addEventListener('change', function() {
            const purchaseType = this.value;
            // Support both purchase-card (old) and purchase-option (new) classes
            const parentLabel = this.closest('.purchase-card') || this.closest('.purchase-option');

            // Update active states for all option styles
            container.querySelectorAll('.purchase-card, .purchase-option, .subscription-toggle__option').forEach(opt => {
              opt.classList.remove('is-active');
              opt.classList.remove('is-selected');
            });
            if (parentLabel) {
              parentLabel.classList.add('is-selected');
              parentLabel.classList.add('is-active');
            }

            // Toggle selling plan based on purchase type
            if (purchaseType === 'subscribe') {
              if (sellingPlanInput) {
                sellingPlanInput.value = originalSellingPlan;
                sellingPlanInput.disabled = false;
              }
            } else {
              if (sellingPlanInput) {
                sellingPlanInput.value = '';
                sellingPlanInput.disabled = true;
              }
            }
          });
        });
      });
    },

    /**
     * Quantity button functionality
     */
    initQuantityButtons: function() {
      const quantityContainers = document.querySelectorAll('.product-quantity');

      quantityContainers.forEach(container => {
        // Skip if already initialized
        if (container.dataset.quantityInitialized) return;
        container.dataset.quantityInitialized = 'true';

        const minusBtn = container.querySelector('[data-quantity-minus]');
        const plusBtn = container.querySelector('[data-quantity-plus]');
        const input = container.querySelector('[data-quantity-input]');

        if (!input) return;

        if (minusBtn) {
          minusBtn.addEventListener('click', () => {
            const currentValue = parseInt(input.value) || 1;
            const minValue = parseInt(input.min) || 1;
            if (currentValue > minValue) {
              input.value = currentValue - 1;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        if (plusBtn) {
          plusBtn.addEventListener('click', () => {
            const currentValue = parseInt(input.value) || 1;
            const maxValue = parseInt(input.max) || 999;
            if (currentValue < maxValue) {
              input.value = currentValue + 1;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      });
    },

    /**
     * Variant selector functionality
     */
    initVariantSelector: function() {
      const productSections = document.querySelectorAll('[data-product-section]');

      productSections.forEach(section => {
        if (section.dataset.variantSelectorInitialized) return;
        section.dataset.variantSelectorInitialized = 'true';

        const form = section.querySelector('[data-product-form]');
        if (!form) return;

        const variantInputs = form.querySelectorAll('[data-option-value]');
        const variantIdInput = form.querySelector('[data-variant-id]');
        const addToCartBtn = form.querySelector('[data-add-to-cart]');
        const subscriptionContainer = section.querySelector('[data-subscription-options]');

        // Price display elements
        const mainPriceDisplay = section.querySelector('[data-current-price]');
        const comparePriceDisplay = section.querySelector('[data-compare-price]');
        const subscribePrice = section.querySelector('[data-subscribe-price]');
        const subscribeCompare = section.querySelector('[data-subscribe-compare]');
        const onetimePrice = section.querySelector('[data-onetime-price]');
        const lowStockEl = section.querySelector('[data-low-stock]');

        // Get subscription discount percentage
        const subscriptionDiscount = subscriptionContainer
          ? parseInt(subscriptionContainer.dataset.discount) || 10
          : 10;

        // Get product data from script tag
        const productScript = document.querySelector('[data-product-json]');
        let productData = null;
        if (productScript) {
          try {
            productData = JSON.parse(productScript.textContent);
          } catch (e) {
            console.error('Error parsing product data:', e);
          }
        }

        // Handle variant option changes
        variantInputs.forEach(input => {
          input.addEventListener('change', () => {
            // Update visual selection on the option
            const optionGroup = input.closest('.product-variant-group__options');
            optionGroup.querySelectorAll('[data-variant-option]').forEach(opt => {
              opt.classList.remove('is-selected');
            });
            const parentLabel = input.closest('[data-variant-option]');
            if (parentLabel) {
              parentLabel.classList.add('is-selected');
            }

            // Get all selected options
            const selectedOptions = [];
            form.querySelectorAll('.product-variant-group__options').forEach((group) => {
              const checked = group.querySelector('input:checked');
              if (checked) {
                selectedOptions.push(checked.value);
              }
            });

            // Find matching variant
            if (productData && productData.variants) {
              const matchingVariant = productData.variants.find(variant => {
                return selectedOptions.every((option, index) => {
                  return variant.options[index] === option;
                });
              });

              if (matchingVariant) {
                // Update variant ID
                if (variantIdInput) {
                  variantIdInput.value = matchingVariant.id;
                }

                // Update URL
                const url = new URL(window.location.href);
                url.searchParams.set('variant', matchingVariant.id);
                window.history.replaceState({}, '', url);

                // Update add to cart button
                if (addToCartBtn) {
                  if (matchingVariant.available) {
                    addToCartBtn.disabled = false;
                    addToCartBtn.textContent = 'Add to Cart';
                  } else {
                    addToCartBtn.disabled = true;
                    addToCartBtn.textContent = 'Sold Out';
                  }
                }

                // Update all price displays
                const formattedPrice = formatMoney(matchingVariant.price);
                const discountedPrice = Math.round(matchingVariant.price * (100 - subscriptionDiscount) / 100);
                const formattedDiscountedPrice = formatMoney(discountedPrice);

                // Main price display
                if (mainPriceDisplay) {
                  mainPriceDisplay.textContent = formattedPrice;
                }

                // Compare at price
                if (comparePriceDisplay) {
                  if (matchingVariant.compare_at_price) {
                    comparePriceDisplay.textContent = formatMoney(matchingVariant.compare_at_price);
                    comparePriceDisplay.style.display = '';
                  } else {
                    comparePriceDisplay.style.display = 'none';
                  }
                }

                // Subscription prices
                if (subscribePrice) {
                  subscribePrice.textContent = formattedDiscountedPrice;
                }
                if (subscribeCompare) {
                  subscribeCompare.textContent = formattedPrice;
                }

                // One-time price
                if (onetimePrice) {
                  onetimePrice.textContent = formattedPrice;
                }

                // Update low stock warning
                if (lowStockEl) {
                  if (matchingVariant.inventory_management && matchingVariant.inventory_quantity > 0 && matchingVariant.inventory_quantity <= 20) {
                    lowStockEl.style.display = 'flex';
                    const stockSpan = lowStockEl.querySelector('span');
                    if (stockSpan) {
                      stockSpan.textContent = `Only ${matchingVariant.inventory_quantity} left in stock`;
                    }
                  } else {
                    lowStockEl.style.display = 'none';
                  }
                }
              }
            }
          });
        });

        function formatMoney(cents) {
          const format = window.SEVO && window.SEVO.moneyFormat ? window.SEVO.moneyFormat : '${{amount}}';
          const value = (cents / 100).toFixed(2);
          return format.replace('{{amount}}', value).replace('{{amount_no_decimals}}', Math.round(cents / 100));
        }
      });
    },

    /**
     * Product gallery functionality
     */
    initGallery: function() {
      const galleries = document.querySelectorAll('[data-product-gallery]');

      galleries.forEach(gallery => {
        // Skip if already initialized
        if (gallery.dataset.galleryInitialized) return;
        gallery.dataset.galleryInitialized = 'true';

        const slides = gallery.querySelectorAll('.product-gallery__slide');
        const thumbs = gallery.querySelectorAll('.product-gallery__thumb');
        const prevBtn = gallery.querySelector('[data-gallery-prev]');
        const nextBtn = gallery.querySelector('[data-gallery-next]');

        if (slides.length <= 1) return;

        let currentIndex = 0;

        // Find initially active slide
        slides.forEach((slide, i) => {
          if (slide.classList.contains('is-active')) {
            currentIndex = i;
          }
        });

        const showSlide = (index) => {
          // Wrap around
          if (index >= slides.length) index = 0;
          if (index < 0) index = slides.length - 1;

          slides.forEach((slide, i) => {
            slide.classList.toggle('is-active', i === index);
          });

          thumbs.forEach((thumb, i) => {
            thumb.classList.toggle('is-active', i === index);
          });

          currentIndex = index;
        };

        // Thumbnail clicks
        thumbs.forEach((thumb, i) => {
          thumb.addEventListener('click', () => showSlide(i));
        });

        // Navigation arrows
        if (prevBtn) {
          prevBtn.addEventListener('click', () => showSlide(currentIndex - 1));
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', () => showSlide(currentIndex + 1));
        }

        // Keyboard navigation
        gallery.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') {
            showSlide(currentIndex - 1);
          } else if (e.key === 'ArrowRight') {
            showSlide(currentIndex + 1);
          }
        });
      });
    },

    /**
     * Parallax scrolling effect
     * Usage: data-parallax="0.2" (speed factor, negative for opposite direction)
     */
    initParallax: function() {
      const parallaxElements = document.querySelectorAll('[data-parallax]');
      if (!parallaxElements.length) return;

      let ticking = false;

      const updateParallax = () => {
        const scrollY = window.pageYOffset;

        parallaxElements.forEach(el => {
          const speed = parseFloat(el.dataset.parallax) || 0.1;
          const rect = el.getBoundingClientRect();
          const elementTop = rect.top + scrollY;
          const windowHeight = window.innerHeight;

          // Only apply parallax when element is in view
          if (scrollY + windowHeight > elementTop && scrollY < elementTop + rect.height) {
            const offset = (scrollY - elementTop) * speed;
            el.style.transform = `translateY(${offset}px)`;
          }
        });

        ticking = false;
      };

      const scrollHandler = () => {
        if (!ticking) {
          requestAnimationFrame(updateParallax);
          ticking = true;
        }
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
      this._scrollHandlers.push(scrollHandler);

      // Initial call
      updateParallax();
    },

    /**
     * Animated count-up for statistics
     * Usage: data-count-to="89" data-count-suffix="%"
     */
    initCountUp: function() {
      const countElements = document.querySelectorAll('[data-count-to]');
      if (!countElements.length) return;

      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5
      };

      const animateCount = (el) => {
        const target = parseFloat(el.dataset.countTo) || 0;
        const duration = parseInt(el.dataset.countDuration) || 2000;
        const suffix = el.dataset.countSuffix || '';
        const prefix = el.dataset.countPrefix || '';
        const decimals = el.dataset.countDecimals ? parseInt(el.dataset.countDecimals) : 0;

        let startTime = null;
        const startValue = 0;

        const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

        const updateCount = (timestamp) => {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          const easedProgress = easeOutQuart(progress);
          const currentValue = startValue + (target - startValue) * easedProgress;

          el.textContent = prefix + currentValue.toFixed(decimals) + suffix;

          if (progress < 1) {
            requestAnimationFrame(updateCount);
          }
        };

        requestAnimationFrame(updateCount);
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !entry.target.dataset.countAnimated) {
            entry.target.dataset.countAnimated = 'true';
            animateCount(entry.target);
          }
        });
      }, observerOptions);

      countElements.forEach(el => observer.observe(el));
    },

    /**
     * Scroll progress indicator
     * Creates a thin gold line at the top that fills as you scroll
     */
    initScrollProgress: function() {
      // Check if progress bar already exists
      if (document.querySelector('.scroll-progress')) return;

      // Create progress bar element
      const progressBar = document.createElement('div');
      progressBar.className = 'scroll-progress';
      document.body.appendChild(progressBar);

      let ticking = false;

      const updateProgress = () => {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollTop = window.pageYOffset;
        const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        progressBar.style.width = progress + '%';
        ticking = false;
      };

      const scrollHandler = () => {
        if (!ticking) {
          requestAnimationFrame(updateProgress);
          ticking = true;
        }
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
      this._scrollHandlers.push(scrollHandler);

      // Initial call
      updateProgress();
    },

    /**
     * Staggered reveal for grid items
     * Usage: data-stagger-container on parent, .stagger-item on children
     */
    initStaggeredReveal: function() {
      const containers = document.querySelectorAll('[data-stagger-container]');
      if (!containers.length) return;

      const observerOptions = {
        root: null,
        rootMargin: '0px 0px -100px 0px',
        threshold: 0.1
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('.stagger-item');
            items.forEach((item, index) => {
              setTimeout(() => {
                item.classList.add('is-visible');
              }, index * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      containers.forEach(container => observer.observe(container));
    },

    /**
     * Smooth scroll for anchor links
     */
    initSmoothScroll: function() {
      // Use event delegation on document to avoid leaking listeners on section reload
      if (this._smoothScrollBound) return;
      this._smoothScrollBound = true;

      document.addEventListener('click', function(e) {
        const anchor = e.target.closest('a[href^="#"]');
        if (!anchor) return;

        const targetId = anchor.getAttribute('href');
        if (targetId === '#') return;

        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          e.preventDefault();
          const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 130;

          window.scrollTo({
            top: targetElement.offsetTop - headerHeight,
            behavior: 'smooth'
          });
        }
      });
    }
  };

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.SEVO.Animations.init();
    });
  } else {
    window.SEVO.Animations.init();
  }

  // Re-initialize on Shopify section reload
  document.addEventListener('shopify:section:load', () => {
    window.SEVO.Animations.init();
  });
})();
